import { useEffect, useState } from 'react'
import {
  hasInstallPrompt, isIos, isStandalone, onInstallAvailability,
  shouldGateToPwa, triggerInstall,
} from '../lib/platform'
import { Button } from './ui'
import './install-prompt.css'

/* Desktop install offer.
 *
 * Phones and tablets are handled by MobileGate, which BLOCKS the browser
 * until the app is installed. A desktop is different: the web app is a
 * perfectly good experience there, so installing is offered rather than
 * demanded - one dismissible card, and dismissing it is remembered.
 *
 * Together the two cover every device: a gadget either gets the mandatory
 * gate or this optional card, so the app can always be installed. */

const DISMISS_KEY = 'ssm.install.dismissed'

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    // Private windows and blocked site-data throw on access - treat that as
    // "not dismissed" rather than letting the whole component fail.
    return false
  }
}

export default function InstallPrompt() {
  const [available, setAvailable] = useState(hasInstallPrompt)
  const [dismissed, setDismissed] = useState(wasDismissed)
  const [installing, setInstalling] = useState(false)
  const [installed, setInstalled] = useState(isStandalone)

  useEffect(() => onInstallAvailability(setAvailable), [])
  useEffect(() => {
    const done = () => setInstalled(true)
    window.addEventListener('appinstalled', done)
    return () => window.removeEventListener('appinstalled', done)
  }, [])

  // Not on a phone/tablet (MobileGate owns those), not already installed,
  // not previously waved away.
  if (shouldGateToPwa() || installed || dismissed) return null

  // Safari on macOS has no beforeinstallprompt but can still add the app to
  // the Dock, so it gets a hint instead of a button. Other browsers with no
  // prompt available (Firefox) get nothing rather than instructions that
  // would not work.
  const macSafari = isIos() === false && /Macintosh/.test(navigator.userAgent)
    && /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|Edg/.test(navigator.userAgent)

  if (!available && !macSafari) return null

  function close() {
    setDismissed(true)
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* nothing to do */ }
  }

  async function install() {
    setInstalling(true)
    const outcome = await triggerInstall()
    setInstalling(false)
    if (outcome === 'accepted') setInstalled(true)
  }

  return (
    <div className="installcard" role="complementary" aria-label="Install SSM Zambia">
      <button type="button" className="installcard__close" onClick={close} aria-label="Not now">
        ×
      </button>
      <div className="installcard__title">Install SSM Zambia</div>
      <p className="installcard__body">
        {macSafari
          ? 'Open the Share menu in Safari and choose “Add to Dock” to keep SSM Zambia one click away.'
          : 'Keep it in your taskbar, open it without a browser tab, and carry on working when the connection drops.'}
      </p>
      {!macSafari && (
        <div className="row" style={{ gap: 'var(--sp-2)' }}>
          <Button size="sm" loading={installing} onClick={install}>Install</Button>
          <Button size="sm" variant="secondary" onClick={close}>Not now</Button>
        </div>
      )}
    </div>
  )
}
